import React, { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Input, Radio, Text } from '@Sage/hoi-poi-ui';

import DateTime from 'containers/components/Fields/DateTime';
import { getLiteral } from 'utils/getLiteral';

import {
    getAdditionalSettingsErrorCodes,
    getAdditionalSettingsPayload,
    SEND_TIME_MODE,
} from './additionalSettings';
import './styles.scss';

const SCHEDULE_INTERVAL_MINUTES = 30;

function getStartOfToday() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
}

const AdditionalSettingsForm = memo(
    ({
        isSaveLoading = false,
        replyTo,
        scheduleAt,
        sendTimeMode,
        useDifferentReplyTo,
        onCancel,
        onReplyToChange,
        onSave,
        onScheduleAtChange,
        onSendTimeModeChange,
        onUseDifferentReplyToChange,
    }) => {
        const [showErrors, setShowErrors] = useState(false);

        const errorCodes = useMemo(
            () =>
                getAdditionalSettingsErrorCodes({
                    replyTo,
                    scheduleAt,
                    sendTimeMode,
                    useDifferentReplyTo,
                }),
            [replyTo, scheduleAt, sendTimeMode, useDifferentReplyTo],
        );
        const errors = useMemo(
            () => ({
                replyTo:
                    errorCodes.replyTo === 'required'
                        ? getLiteral('helptext_required_field')
                        : errorCodes.replyTo === 'invalid'
                          ? getLiteral('label_invalid_email')
                          : undefined,
                scheduleAt:
                    errorCodes.scheduleAt === 'invalid'
                        ? getLiteral('label_invalid_date')
                        : undefined,
            }),
            [errorCodes],
        );
        const isValid = Object.keys(errorCodes).length === 0;

        const handleSendImmediately = useCallback(
            (event) => {
                event?.stopPropagation();
                onSendTimeModeChange(SEND_TIME_MODE.IMMEDIATELY);
            },
            [onSendTimeModeChange],
        );

        const handleScheduleForSpecificTime = useCallback(
            (event) => {
                event?.stopPropagation();
                onSendTimeModeChange(SEND_TIME_MODE.SCHEDULED);
            },
            [onSendTimeModeChange],
        );

        const handleSave = useCallback(() => {
            setShowErrors(true);
            if (!isValid) {
                return;
            }

            onSave?.({
                ...getAdditionalSettingsPayload({
                    replyTo,
                    scheduleAt,
                    sendTimeMode,
                    useDifferentReplyTo,
                }),
                sendTimeMode,
                useDifferentReplyTo,
            });
        }, [isValid, onSave, replyTo, scheduleAt, sendTimeMode, useDifferentReplyTo]);

        const handleCancel = useCallback(() => {
            setShowErrors(false);
            onCancel?.();
        }, [onCancel]);

        const handleReplyToCheckboxClick = useCallback((event) => {
            event.stopPropagation();
        }, []);

        return (
            <div className="additional-settings-form">
                <div className="additional-settings-form__section">
                    <Text
                        medium
                        type="subtitle1"
                        className="additional-settings-form__section-title"
                    >
                        {getLiteral('label_marketing_campaigns_email_config_email_sending')}
                    </Text>
                    <div
                        className="additional-settings-form__checkbox-row"
                        onClick={onUseDifferentReplyToChange}
                    >
                        <Checkbox
                            checked={useDifferentReplyTo}
                            color="actionMinor"
                            onChange={onUseDifferentReplyToChange}
                            onClick={handleReplyToCheckboxClick}
                        />
                        <Text type="body">
                            {getLiteral('label_marketing_campaigns_email_config_reply_to_address')}
                        </Text>
                    </div>
                    {useDifferentReplyTo && (
                        <Input
                            className="additional-settings-form__reply-to"
                            isFullWidth
                            placeholder={getLiteral(
                                'label_marketing_campaigns_email_config_reply_to_address_placeholder',
                            )}
                            value={replyTo}
                            onChange={onReplyToChange}
                            error={showErrors ? errors.replyTo : undefined}
                        />
                    )}
                </div>

                <div className="additional-settings-form__section">
                    <Text
                        medium
                        type="subtitle1"
                        className="additional-settings-form__section-title"
                    >
                        {getLiteral('label_marketing_campaigns_email_config_send_time')}
                    </Text>
                    <div className="additional-settings-form__radio-options">
                        <div
                            className="additional-settings-form__radio-row"
                            onClick={handleSendImmediately}
                        >
                            <Radio
                                checked={sendTimeMode === SEND_TIME_MODE.IMMEDIATELY}
                                onChange={handleSendImmediately}
                            />
                            <Text type="body">
                                {getLiteral(
                                    'label_marketing_campaigns_email_config_send_immediately',
                                )}
                            </Text>
                        </div>
                        <div
                            className="additional-settings-form__radio-row"
                            onClick={handleScheduleForSpecificTime}
                        >
                            <Radio
                                checked={sendTimeMode === SEND_TIME_MODE.SCHEDULED}
                                onChange={handleScheduleForSpecificTime}
                            />
                            <Text type="body">
                                {getLiteral('label_marketing_campaigns_email_config_schedule_time')}
                            </Text>
                        </div>
                    </div>
                    {sendTimeMode === SEND_TIME_MODE.SCHEDULED && (
                        <DateTime
                            className="additional-settings-form__schedule-at"
                            datePlaceholder={getLiteral('label_select_date')}
                            error={showErrors ? errors.scheduleAt : undefined}
                            interval={SCHEDULE_INTERVAL_MINUTES}
                            isMinTimeNow
                            labelMode="vertical"
                            minDate={getStartOfToday()}
                            onChange={onScheduleAtChange}
                            value={scheduleAt}
                        />
                    )}
                </div>

                <div className="additional-settings-form__actions">
                    <Button type="secondary" size="small" onClick={handleCancel}>
                        {getLiteral('action_cancel')}
                    </Button>
                    <Button
                        type="primary"
                        size="small"
                        isDisabled={isSaveLoading}
                        isLoading={isSaveLoading}
                        onClick={handleSave}
                    >
                        {getLiteral('action_save')}
                    </Button>
                </div>
            </div>
        );
    },
);

AdditionalSettingsForm.displayName = 'AdditionalSettingsForm';

AdditionalSettingsForm.propTypes = {
    isSaveLoading: PropTypes.bool,
    replyTo: PropTypes.string,
    scheduleAt: PropTypes.instanceOf(Date),
    sendTimeMode: PropTypes.oneOf(Object.values(SEND_TIME_MODE)).isRequired,
    useDifferentReplyTo: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    onReplyToChange: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onScheduleAtChange: PropTypes.func.isRequired,
    onSendTimeModeChange: PropTypes.func.isRequired,
    onUseDifferentReplyToChange: PropTypes.func.isRequired,
};

export default AdditionalSettingsForm;
